import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { BrowserRouter } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { GuestConversionProvider } from "@/contexts/GuestConversionContext";
import { AnimatedRoutes } from "@/components/AnimatedRoutes";
import { AuthRecoveryRedirect } from "./components/AuthRecoveryRedirect";
import { useEditorSettings } from "@/hooks/useEditorSettings";
import { THESIS_CLEAN_CHROME } from "@/lib/thesisCleanChrome";

const AppCommandPalette = lazy(() =>
  import("@/components/AppCommandPalette").then((m) => ({ default: m.AppCommandPalette })),
);
const FloatingCommandDock = lazy(() =>
  import("@/components/FloatingCommandDock").then((m) => ({ default: m.FloatingCommandDock })),
);
const SpotifyPlayer = lazy(() =>
  import("@/components/editor/SpotifyPlayer").then((m) => ({ default: m.SpotifyPlayer })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppInner() {
  const { loading, user } = useAuth();
  const { settings } = useEditorSettings();
  const show_spotify = !THESIS_CLEAN_CHROME && !!user && settings.spotifyEnabled;
  const show_dock = !THESIS_CLEAN_CHROME;

  if (loading) {
    return (
      <>
        <AuthRecoveryRedirect />
        {show_dock && <Suspense fallback={null}><FloatingCommandDock /></Suspense>}
        {show_spotify && <Suspense fallback={null}><SpotifyPlayer /></Suspense>}
        <main>
          <AnimatedRoutes />
        </main>
      </>
    );
  }

    return (
      <>
        <AuthRecoveryRedirect />
        <Suspense fallback={null}><AppCommandPalette /></Suspense>
        {show_dock && <Suspense fallback={null}><FloatingCommandDock /></Suspense>}
        {show_spotify && <Suspense fallback={null}><SpotifyPlayer /></Suspense>}
        <main>
          <AnimatedRoutes />
        </main>
      </>
    );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem storageKey="formatafacil-theme">
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <GuestConversionProvider>
              <AppInner />
            </GuestConversionProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
